import { pageOffset, type StatementList, type StatementRow } from '@myrewards/contracts';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Fragment, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-aria-components';
import { isApiError, meQuery } from '../../api';
import {
  decideStatement,
  settingQuery,
  shareworksKeys,
  statementKeys,
  statementsQuery,
} from '../../api/close';
import { documentHref, queueKeys } from '../../api/ops';
import { NoMatches } from '../../ops/queue/NoMatches';
import { SettingChange } from '../../ops/SettingChange';
import { AmountInput, BackLink, CountInput, Notice, PersonChip, TickBox } from '../../ops/shared';
import { Hero, PageBody, TwoColumns } from '../../shell';
import {
  Button,
  Card,
  clientPage,
  ConfirmDialog,
  cx,
  EmptyState,
  FilterBar,
  formatCount,
  formatMoney,
  formatShortDate,
  Icon,
  Pager,
  SortByMenu,
  SortHead,
  StatusPill,
  TextField,
  tableCards,
  useCardRows,
  useClampedPage,
  useListTable,
  usePaging,
  useScrollRegion,
  useToast,
} from '../../ui';
import { closeProblemWords, netWords, paysAsWords } from '../labels';
import {
  estimateNote,
  NO_ESTIMATE,
  fileNameParts,
  firstToSettle,
  fitCheck,
  matchStatus,
  nextStepWords,
  overrideBlocker,
  overrideDraft,
  overrideSubject,
  rowAction,
  STATEMENT_SORT_COLUMNS,
  STATEMENTS_DEFAULT_SORT,
  STATEMENTS_DEFAULT_WORDS,
  statementFilters,
  statementsTie,
  settleSentence,
  settleTiles,
  type OverrideDraft,
} from './presenter';
import o from '../../ops/Ops.module.css';
import s from '../Close.module.css';
import { QUEUE_VIEW, WORK_HREF } from '../../ops/labels';
import { ManageTabs } from '../../manage/ManageTabs';

/** "$2,912.00" with a marker when Payroll's figure differs, so it's never colour alone. */
function Final({ value, differs }: { value: string; differs: boolean }): ReactElement {
  return (
    <span className={cx(differs && s.differs)}>
      {value}
      {differs && <span className="visually-hidden"> (differs from the estimate)</span>}
    </span>
  );
}

function sharesWords(n: number): string {
  return n === 0 ? 'None' : formatCount(n);
}

function useStatementsChanged(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    // Every line on an award shares its version: read the whole list again.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: statementKeys.list }),
      queryClient.invalidateQueries({ queryKey: queueKeys.all }),
      queryClient.invalidateQueries({ queryKey: shareworksKeys.list }),
    ]);
  };
}

function ActionCell({
  row,
  meId,
  processes,
  onConfirm,
  onOverride,
}: {
  row: StatementRow;
  meId: string;
  processes: boolean;
  onConfirm: () => void;
  onOverride: () => void;
}): ReactElement {
  const name = row.person.name;
  const action = rowAction(row, meId);
  // A System Admin sees the check without making it (APP-134).
  if (!processes && (action === 'confirm' || action === 'override'))
    return <span className={o.muted}>For the Stock Admin team</span>;
  switch (action) {
    case 'confirm':
      return (
        <Button size="sm" onPress={onConfirm} aria-label={`Confirm the statement for ${name}`}>
          Confirm
        </Button>
      );
    case 'override':
      return (
        <Button
          size="sm"
          variant="ghost"
          onPress={onOverride}
          aria-label={`Settle Payroll’s numbers for ${name}`}
        >
          Settle
        </Button>
      );
    case 'held':
      return row.claimedBy === null ? (
        <span className={o.muted}>Someone else is on it</span>
      ) : (
        <PersonChip
          initials={row.claimedBy.initials}
          name={row.claimedBy.name}
          detail="On it now"
        />
      );
    case 'waiting':
      return <span className={o.muted}>Waiting on Payroll</span>;
    case 'done':
      return <span className={o.muted}>Done</span>;
  }
}

function LinesTable({
  list,
  meId,
  selected,
  onConfirm,
  onOverride,
}: {
  list: StatementList;
  meId: string;
  selected: string | null;
  onConfirm: (row: StatementRow) => void;
  onOverride: (row: StatementRow) => void;
}): ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(scrollRef, 'Paid cash lines');
  // Card rows below 720 px (QA-030): each line is a card, its next step at the bottom.
  const c = tableCards(useCardRows());
  // APP-149: the lines sort by their headings and filter by match and award type, over the whole
  // list before it's paged. The queue's views keep one saved sort (B6).
  const barRef = useRef<HTMLDivElement>(null);
  const filters = useMemo(() => statementFilters(list.rows), [list.rows]);
  const table = useListTable({
    table: 'queue',
    rows: list.rows,
    columns: STATEMENT_SORT_COLUMNS,
    defaultSort: STATEMENTS_DEFAULT_SORT,
    defaultWords: STATEMENTS_DEFAULT_WORDS,
    filters,
    tieBreak: statementsTie,
    barRef,
  });
  const sorting = list.rows.length > 0;
  // APP-135: the whole list is on the screen; 50 lines show at a time.
  const paging = usePaging('statements');
  const { anchorRef } = paging;
  const paged = clientPage(table.rows, paging.page, paging.size);
  useClampedPage(paging, paged.page.number);
  /**
   * A heading that sorts the lines (D3); plain words when there are none or in card rows. A
   * function, not a component, so a sorted heading keeps its focus.
   */
  const heading = (id: string, words: ReactNode, className?: string): ReactElement => {
    const numeric = STATEMENT_SORT_COLUMNS.find((column) => column.id === id)?.numeric === true;
    return sorting ? (
      <SortHead
        column={id}
        sort={table.sort}
        onSortChange={table.setSort}
        numeric={numeric}
        plain={c.on}
        pad={7}
        className={className}
        {...c.header}
      >
        {words}
      </SortHead>
    ) : (
      <th scope="col" className={className} {...c.header}>
        {words}
      </th>
    );
  };
  return (
    <>
      {sorting && (
        <div ref={barRef}>
          <FilterBar label="Filter the paid cash lines" {...table.bar} />
        </div>
      )}
      <section ref={anchorRef} tabIndex={-1}>
        {/* Card rows have no headings to press: the Sort by menu takes their place. */}
        {c.on && sorting && (
          <SortByMenu
            columns={STATEMENT_SORT_COLUMNS}
            rows={list.rows}
            sort={table.sort}
            onSortChange={table.setSort}
          />
        )}
        <div ref={scrollRef} className={s.tableScroll} {...(c.on ? {} : region)}>
          <table {...c.table(cx(s.table, s.wide))}>
            <caption className="visually-hidden">Paid cash lines</caption>
            <thead {...c.head}>
              {!c.on && (
                <tr className={s.bandRow}>
                  <td colSpan={4} />
                  <th scope="colgroup" colSpan={4} className={s.band}>
                    App estimate
                  </th>
                  <th scope="colgroup" colSpan={3} className={s.band}>
                    Payroll final
                  </th>
                  <td colSpan={3} />
                </tr>
              )}
              <tr {...c.row}>
                {heading('award', 'Award')}
                {heading('employee', 'Employee')}
                {heading('paysAs', 'Pays as')}
                {heading(
                  'cash',
                  <>
                    <span className="visually-hidden">Estimate </span>Cash
                  </>,
                  s.num,
                )}
                {heading(
                  'estimateTax',
                  <>
                    <span className="visually-hidden">Estimate </span>Tax
                  </>,
                  cx(s.num, s.bandStart),
                )}
                {heading(
                  'estimateCash',
                  <>
                    <span className="visually-hidden">Estimate </span>Net cash
                  </>,
                  s.num,
                )}
                {heading(
                  'estimateShares',
                  <>
                    <span className="visually-hidden">Estimate </span>Shares
                  </>,
                  s.num,
                )}
                {heading(
                  'payrollTax',
                  <>
                    <span className="visually-hidden">Payroll </span>Tax
                  </>,
                  cx(s.num, s.bandStart),
                )}
                {heading(
                  'payrollCash',
                  <>
                    <span className="visually-hidden">Payroll </span>Net cash
                  </>,
                  s.num,
                )}
                {heading(
                  'payrollShares',
                  <>
                    <span className="visually-hidden">Payroll </span>Shares
                  </>,
                  s.num,
                )}
                <th scope="col" className={s.bandStart} {...c.header}>
                  Earning statement
                </th>
                {heading('match', 'Match')}
                <th scope="col" {...c.header}>
                  <span className="visually-hidden">Next step</span>
                </th>
              </tr>
            </thead>
            <tbody {...c.body}>
              {list.rows.length === 0 && (
                <tr className={s.emptyRow} {...c.row}>
                  <td colSpan={14} {...c.wide}>
                    <EmptyState
                      variant="first-day"
                      art="letter"
                      size="sm"
                      headline="No paid cash lines yet"
                      body="When Payroll pays a cash award, it lands here with Payroll’s figures beside the earning statement and, for a PAA, the team’s estimate, so you can check them line by line."
                    />
                  </td>
                </tr>
              )}
              {sorting && table.rows.length === 0 && (
                <tr className={s.emptyRow} {...c.row}>
                  <td colSpan={14} {...c.wide}>
                    <NoMatches what="lines" onClear={table.clearFromEmpty} />
                  </td>
                </tr>
              )}
              {paged.rows.map((row) => {
                const pays = paysAsWords(row.payAs, row.type);
                const status = matchStatus(row);
                return (
                  <tr
                    key={row.lineId}
                    className={cx(
                      row.status === 'differs' && s.problemRow,
                      selected === row.lineId && s.openRow,
                    )}
                    {...c.row}
                  >
                    <td {...c.cell('Award', 'title')}>
                      <Link href={`/awards/${row.award.id}`} className={cx(o.strongLink, s.nowrap)}>
                        {row.award.displayId}
                      </Link>
                    </td>
                    <td {...c.cell('Employee')}>
                      <span className={s.cellStrong}>{row.person.name}</span>
                      {row.person.eeNumber !== null && (
                        <span className={o.sub}>EE# {row.person.eeNumber}</span>
                      )}
                    </td>
                    <td {...c.cell('Pays as')}>
                      <span className={s.cellStrong}>{pays.label}</span>
                      <span className={o.sub}>{pays.detail}</span>
                    </td>
                    <td {...c.cell('Cash', 'value', s.num)}>{formatMoney(row.grossCashCents)}</td>
                    {row.estimate === null ? (
                      // A cash-only line has no app estimate (APP-101): one cell says so.
                      <td
                        colSpan={3}
                        {...c.cell('App estimate', 'value', cx(o.muted, s.bandStart))}
                      >
                        No estimate · cash only
                      </td>
                    ) : (
                      <>
                        <td {...c.cell('Estimate tax', 'value', cx(s.num, s.bandStart))}>
                          {formatMoney(row.estimate.taxCents)}
                        </td>
                        <td {...c.cell('Estimate net cash', 'value', s.num)}>
                          {formatMoney(row.estimate.cashCents)}
                        </td>
                        <td {...c.cell('Estimate shares', 'value', s.num)}>
                          {sharesWords(row.estimate.shares)}
                        </td>
                      </>
                    )}
                    <td {...c.cell('Payroll tax', 'value', cx(s.num, s.bandStart))}>
                      <Final
                        value={formatMoney(row.final.taxCents)}
                        differs={row.differs?.tax ?? false}
                      />
                    </td>
                    <td {...c.cell('Payroll net cash', 'value', s.num)}>
                      <Final
                        value={formatMoney(row.final.cashCents)}
                        differs={row.differs?.cash ?? false}
                      />
                    </td>
                    <td {...c.cell('Payroll shares', 'value', s.num)}>
                      <Final
                        value={sharesWords(row.final.shares)}
                        differs={row.differs?.shares ?? false}
                      />
                    </td>
                    <td {...c.cell('Earning statement', 'value', s.bandStart)}>
                      {row.statement === null ? (
                        <span className={o.muted}>
                          Not attached yet · paid {formatShortDate(row.paidAt)}
                        </span>
                      ) : row.status === 'waiting_on_statement' ? (
                        <span className={o.muted}>
                          {row.statement.fileName} · being checked for viruses
                        </span>
                      ) : (
                        <>
                          <a
                            href={documentHref(row.award.id, row.statement.documentId)}
                            className={cx(o.inlineLink, s.fileLink)}
                            aria-label={`Download ${row.statement.fileName}, the earning statement for ${row.person.name}`}
                            download
                          >
                            <Icon name="file" size={16} />
                            <span className={s.fileName}>
                              {fileNameParts(row.statement.fileName).map((part, i) => (
                                <Fragment key={i}>
                                  {i > 0 && <wbr />}
                                  <span className={s.nowrap}>{part}</span>
                                </Fragment>
                              ))}
                            </span>
                          </a>
                          <span className={o.sub}>
                            Attached
                            {row.statement.attachedBy === null
                              ? ''
                              : ` by ${row.statement.attachedBy.name}`}{' '}
                            · {formatShortDate(row.statement.attachedAt)}
                          </span>
                        </>
                      )}
                    </td>
                    <td {...c.cell('Match')}>
                      <StatusPill size="sm" tone={status.tone} label={status.label} />
                    </td>
                    <td
                      {...c.cell(
                        'Next step',
                        // A button goes to the card's foot; a status sentence keeps its label.
                        list.processes && ['confirm', 'override'].includes(rowAction(row, meId))
                          ? 'action'
                          : 'value',
                      )}
                    >
                      <ActionCell
                        row={row}
                        meId={meId}
                        processes={list.processes}
                        onConfirm={() => {
                          onConfirm(row);
                        }}
                        onOverride={() => {
                          onOverride(row);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <Pager
        label="Paid cash lines"
        page={paged.page}
        onPage={(n) => {
          paging.setPage(n);
        }}
        onSize={(size) => {
          paging.setSize(size, pageOffset(paged.page));
        }}
      />
    </>
  );
}

function OverridePanel({
  row,
  meId,
  problem,
  onRecorded,
  onProblem,
  processes,
}: {
  row: StatementRow;
  meId: string;
  problem: string | null;
  processes: boolean;
  onRecorded: (row: StatementRow) => void;
  onProblem: (message: string | null) => void;
}): ReactElement {
  const changed = useStatementsChanged();
  const [draft, setDraft] = useState<OverrideDraft>(() => overrideDraft(row));
  // The award's FMV: the shares are valued at it.
  const fmv = row.fmvCents;
  const fit = fitCheck(row, draft, fmv);
  const blocker = overrideBlocker(draft, fit);
  const action = rowAction(row, meId);

  const record = useMutation({
    mutationFn: () =>
      decideStatement(row.lineId, {
        decision: 'override',
        version: row.version,
        finalCashCents: draft.cashCents ?? 0,
        finalTaxCents: draft.taxCents ?? 0,
        finalShares: draft.shares ?? 0,
        reason: draft.reason.trim(),
        agreedWithPayroll: true,
      }),
    onSuccess: async ({ row: done }) => {
      onProblem(null);
      await changed();
      onRecorded(done);
    },
    onError: async (error) => {
      onProblem(closeProblemWords(error));
      if (isApiError(error) && error.status === 409) await changed();
    },
  });
  const set = (patch: Partial<OverrideDraft>): void => {
    setDraft((d) => ({ ...d, ...patch }));
  };

  return (
    <div className={s.override}>
      <div className={s.tilesRow}>
        {settleTiles(row).map((tile) => (
          <div key={tile.label} className={cx(s.figure, tile.differs && s.figureDiffers)}>
            <p className={cx('eyebrow', s.figureLabel)}>
              {row.person.name} · {tile.label}
            </p>
            <p className={s.figureEstimate}>Estimate {tile.estimate}</p>
            <p className={cx('disp', s.figureValue)}>{tile.payroll}</p>
            <p className={s.figureNote}>Payroll{tile.differs ? ' · differs' : ''}</p>
          </div>
        ))}
      </div>
      <p className={o.cardText}>{settleSentence(row, fmv)}</p>
      {action === 'held' ? (
        <Notice tone="info">
          {row.claimedBy?.name ?? 'Someone else'} is on this award’s statement check now.
        </Notice>
      ) : !processes ? (
        <Notice tone="info">
          The Stock Admin team agrees these numbers with Payroll and records them here.
        </Notice>
      ) : (
        <form
          className={s.overrideForm}
          onSubmit={(event) => {
            event.preventDefault();
            if (blocker === null) record.mutate();
          }}
        >
          <div className={s.fieldRow}>
            <div className={s.field}>
              <span className={s.fieldLabel}>Final cash paid</span>
              <AmountInput
                label={`Final cash paid to ${row.person.name}`}
                valueCents={draft.cashCents}
                onChange={(cashCents) => {
                  set({ cashCents });
                }}
                wholeDollars={false}
              />
            </div>
            <div className={s.field}>
              <span className={s.fieldLabel}>Final tax</span>
              <AmountInput
                label={`Final tax for ${row.person.name}`}
                valueCents={draft.taxCents}
                onChange={(taxCents) => {
                  set({ taxCents });
                }}
                wholeDollars={false}
              />
            </div>
            <div className={s.field}>
              <span className={s.fieldLabel}>Final share count</span>
              <CountInput
                label={`Final share count for ${row.person.name}`}
                value={draft.shares}
                onChange={(shares) => {
                  set({ shares });
                }}
              />
            </div>
          </div>
          {fit === 'off' && (
            <Notice tone="problem">
              The cash paid, the shares at FMV and the tax withheld need to add up to the award’s
              cash ({formatMoney(row.grossCashCents)}
              {row.payAs === 'b' ? ', the employee pays the tax' : ''}).
            </Notice>
          )}
          {fit === 'fits' && (
            <Notice tone="done">
              The figures add up to the award’s cash:{' '}
              {netWords({ cashCents: draft.cashCents ?? 0, shares: draft.shares ?? 0 })} to{' '}
              {row.person.name}.
            </Notice>
          )}
          <TextField
            label="Reason (required)"
            multiline
            value={draft.reason}
            onChange={(reason) => {
              set({ reason });
            }}
            isRequired
          />
          <TickBox
            isSelected={draft.agreed}
            onChange={(agreed) => {
              set({ agreed });
            }}
          >
            <span className={s.tickText}>
              <span className={o.strong}>Agreed with Payroll</span>
              <span className={o.sub}>Payroll agreed these final numbers</span>
            </span>
          </TickBox>
          <div className={s.buttonRow}>
            <Button type="submit" isDisabled={blocker !== null} isPending={record.isPending}>
              Record final cash, tax and shares
            </Button>
            {blocker !== null && <span className={o.muted}>{blocker}</span>}
          </div>
          {problem !== null && <Notice tone="problem">{problem}</Notice>}
        </form>
      )}
    </div>
  );
}

function StatementOnFile({ row }: { row: StatementRow | null }): ReactElement {
  const help = {
    shows:
      'The earning statement Payroll attached to the line you’re looking at, with when it was paid.',
    canDo:
      'Download the statement to compare it with the figures. Payroll attaches a new one if it’s wrong.',
  };
  if (row === null || row.statement === null)
    return (
      <Card title="Statement on file" help={help} compact>
        <EmptyState
          variant="first-day"
          art="letter"
          size="sm"
          headline="No statements yet"
          body="Payroll attaches an earning statement PDF to every cash award it pays. The latest one shows here."
        />
      </Card>
    );
  const pays = paysAsWords(row.payAs, row.type);
  const readable = row.status !== 'waiting_on_statement';
  return (
    <Card title="Statement on file" help={help} compact>
      <div className={s.fileBox}>
        <Icon name="file" size={20} />
        <span className={s.fileText}>
          {readable ? (
            <a
              href={documentHref(row.award.id, row.statement.documentId)}
              className={o.inlineLink}
              aria-label={`Download ${row.statement.fileName}, the earning statement for ${row.person.name}`}
              download
            >
              {row.statement.fileName}
            </a>
          ) : (
            <span className={o.strong}>{row.statement.fileName}</span>
          )}
          <span className={o.sub}>
            Earning statement · attached
            {row.statement.attachedBy === null ? '' : ` by ${row.statement.attachedBy.name}`} ·{' '}
            {formatShortDate(row.statement.attachedAt)}
            {readable ? '' : ' · being checked for viruses'}
          </span>
        </span>
      </div>
      <dl className={s.factList}>
        <div>
          <dt>Paid</dt>
          <dd>{formatShortDate(row.paidAt)}</dd>
        </div>
        <div>
          <dt>Pays as</dt>
          <dd>{pays.label}</dd>
        </div>
        <div>
          <dt>Also on the award</dt>
          <dd>{row.options > 0 ? `${formatCount(row.options)} options` : 'No options'}</dd>
        </div>
      </dl>
    </Card>
  );
}

/** SA6: every paid cash line's statement against the app's estimate. */
export function StatementsPage(): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const { data: list } = useSuspenseQuery(statementsQuery);
  // The banner's setting, with who last changed it.
  const required = useQuery(settingQuery('payroll.earningStatementRequired'));
  const toast = useToast();
  const changed = useStatementsChanged();
  const meId = me.person.id;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<StatementRow | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [rowProblem, setRowProblem] = useState<string | null>(null);

  const selected =
    list.rows.find((r) => r.lineId === selectedId && r.status === 'differs') ??
    firstToSettle(list, meId);
  const onFile =
    (selectedId === null ? null : list.rows.find((r) => r.lineId === selectedId)) ??
    selected ??
    list.rows.find((r) => r.statement !== null) ??
    null;

  const confirm = useMutation({
    mutationFn: (row: StatementRow) =>
      decideStatement(row.lineId, { decision: 'confirm', version: row.version }),
    onSuccess: async ({ row }) => {
      setRowProblem(null);
      await changed();
      toast.show({
        tone: 'done',
        title: `${row.person.name}’s statement confirmed`,
        body: `${row.award.displayId} · ${row.status === 'to_shareworks' ? 'on to the Shareworks worklist' : 'closed'}.`,
      });
    },
    onError: async (error, row) => {
      setRowProblem(closeProblemWords(error));
      if (isApiError(error) && error.code === 'numbers_differ') setSelectedId(row.lineId);
      if (isApiError(error) && error.status === 409) await changed();
    },
  });

  return (
    <>
      <Hero
        eyebrow="Payroll status · Stock Admin"
        title="Earning statements"
        sub="Check what Payroll paid against the earning statement and, for a PAA, the team’s estimate. When a share count differs, agree an override with Payroll and record the final figures."
        stats={[
          {
            label: 'All sectors · numbers differ',
            value: formatCount(list.counts.differ),
            meta:
              list.rows.length === 0
                ? 'Nothing paid yet'
                : list.counts.differ === 0
                  ? 'Nothing to settle with Payroll'
                  : 'Override with Payroll to settle',
            ...(list.counts.differ > 0 && { tone: 'gold' as const }),
          },
          {
            label: 'All sectors · statements missing',
            value: formatCount(list.counts.missing),
            meta:
              list.rows.length === 0
                ? 'Nothing paid yet'
                : list.counts.missing === 0
                  ? 'Every paid line has its statement'
                  : 'Paid · waiting on Payroll',
          },
        ]}
        titleTestId="statements-title"
      />
      <ManageTabs current="earning-statements" />
      <PageBody>
        <div className={o.stack}>
          <BackLink href={QUEUE_VIEW.queue.href}>Work queue</BackLink>
          <div className={s.banner}>
            <Icon name="file" size={18} />
            <p className={s.bannerText}>
              <span>
                {list.statementRequired
                  ? 'An earning statement is required before a cash award closes.'
                  : 'An earning statement is optional: a cash award can close without one.'}
              </span>{' '}
              {required.data !== undefined && (
                <span className={s.bannerChange}>
                  <SettingChange
                    view={required.data}
                    title="Earning statement before a cash award closes"
                    subject="History · Payroll"
                    words={(v) => (v === true ? 'Required' : 'Optional')}
                  />
                </span>
              )}
            </p>
          </div>
          <Card
            title="Paid cash lines"
            helpAnchor="statement-lines"
            help={{
              shows:
                'Every cash line Payroll paid in the last month: Payroll’s final numbers from the earning statement and, for a PAA, the team’s estimate beside them, and whether they match.',
              canDo:
                'Sort by a heading or filter by match and award type. Confirm a statement that matches. When Payroll’s final tax changed an elected share count, settle the numbers: record Payroll’s figures with a reason, agreed with Payroll.',
            }}
            aside={<span className={o.filterNote}>{estimateNote()}</span>}
          >
            {rowProblem !== null && <Notice tone="problem">{rowProblem}</Notice>}
            {
              <LinesTable
                list={list}
                meId={meId}
                selected={selected?.lineId ?? null}
                onConfirm={(row) => {
                  setSelectedId(row.lineId);
                  setConfirming(row);
                }}
                onOverride={(row) => {
                  setSelectedId(row.lineId);
                  setProblem(null);
                  // Take the person to the panel, keyboard focus included.
                  const panel = document.getElementById('override');
                  panel?.scrollIntoView({ block: 'start' });
                  panel?.focus({ preventScroll: true });
                }}
              />
            }
          </Card>
          <TwoColumns
            sideLabel="The statement"
            main={
              <div id="override" className={s.anchor} tabIndex={-1}>
                <Card
                  title="Override with Payroll’s numbers"
                  helpAnchor="statement-override"
                  {...(selected !== null && { accent: 'gold' as const })}
                  badge={
                    <StatusPill
                      size="sm"
                      tone={selected === null ? 'neutral' : 'problem'}
                      label={selected === null ? 'Nothing to settle' : 'Numbers differ'}
                    />
                  }
                  help={{
                    shows:
                      'When Payroll’s final tax changed an elected share count, the line opens here: the estimate beside Payroll’s numbers, and why they differ.',
                    canDo:
                      'Agree the final cash, tax and shares with Payroll, then record them with a reason. The figures must add up to the award’s cash.',
                  }}
                >
                  {selected === null ? (
                    <EmptyState
                      variant="first-day"
                      art="shield"
                      headline="Nothing to reconcile"
                      body="When Payroll’s final tax or share count differs from the estimate, the line opens here so you and Payroll can agree the final numbers and record them with a reason."
                    />
                  ) : (
                    <>
                      <p className={cx('eyebrow', s.eyebrowLine)}>{overrideSubject(selected)}</p>
                      <OverridePanel
                        key={`${selected.lineId}-${String(selected.version)}`}
                        row={selected}
                        meId={meId}
                        processes={list.processes}
                        problem={problem}
                        onProblem={setProblem}
                        onRecorded={(row) => {
                          setSelectedId(null);
                          toast.show({
                            tone: 'done',
                            title: `Payroll’s numbers recorded for ${row.person.name}`,
                            body: `${row.award.displayId} · ${nextStepWords(row, row.final.shares).toLowerCase()}.`,
                          });
                        }}
                      />
                    </>
                  )}
                </Card>
              </div>
            }
            side={
              <>
                <StatementOnFile row={onFile} />
                <Card
                  title="After the match"
                  tone="dark"
                  compact
                  help={{
                    shows:
                      'Where a line goes once its statement is confirmed or its numbers recorded.',
                    canDo: 'Open the Shareworks worklist to enter the grants.',
                  }}
                >
                  <p className={o.onDarkNote}>
                    Shares bought through the election move to the Shareworks worklist with the
                    final count. Cash-only lines close once the statement is on file.
                  </p>
                  <Link href={WORK_HREF.shareworks} className={o.onDarkLink}>
                    Next: Shareworks worklist
                  </Link>
                </Card>
              </>
            }
          />
        </div>
      </PageBody>
      <ConfirmDialog
        isOpen={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={confirming === null ? 'Confirm?' : `Confirm ${confirming.person.name}’s statement?`}
        intro={
          confirming?.estimate === null
            ? `${NO_ESTIMATE} Confirming records the check with who and when.`
            : 'The statement matches the app’s estimate. Confirming records the check with who and when.'
        }
        summary={
          confirming === null
            ? []
            : [
                {
                  label: 'Line',
                  value: `${confirming.award.displayId} · ${confirming.person.name}`,
                },
                {
                  label: 'Payroll final',
                  value: `Tax ${formatMoney(confirming.final.taxCents)} · ${netWords(confirming.final)}`,
                },
                { label: 'Next', value: nextStepWords(confirming, confirming.final.shares) },
              ]
        }
        withoutReason
        confirmLabel="Confirm the statement"
        onConfirm={async () => {
          if (confirming === null) return;
          await confirm.mutateAsync(confirming).catch(() => undefined);
        }}
      />
    </>
  );
}
